import {InferGetStaticPropsType} from 'next';
import Image from 'next/legacy/image';

import cropLeaves from '../../../public/img/crop-leaves.png';
import cropLeavesVert from '../../../public/img/crop-leaves-vert.png';
import Col from '../../features/common/Col';
import {useEnvironment} from '../../features/common/EnvironmentProvider';
import Row from '../../features/common/Row';
import {useBreakpoint} from '../../features/common/util/useBreakpoint';
import {getCmsEntry} from '../../features/strapi';
import {ApiAboutAbout, ApiFooterFooter} from '../../features/strapi/generated/contentTypes';
import AboutNavigation from '../../features/tool/components/AboutNavigation';
import Footer from '../../features/tool/components/Footer';
import InfoCard from '../../features/tool/components/InfoCard';
import {getToolLayout} from '../../features/tool/components/Layout';
import Newsletter from '../../features/tool/components/Newsletter';
import PartnerLogos from '../../features/tool/components/PartnerLogos';
import RichText from '../../features/tool/components/RichText';
import icons, {IconId} from '../../features/tool/icons';
import {buildStaticPropsResponse} from '../../features/tool/util/dataFetch';
import {NextPageWithLayout} from '../_app';

const AboutPage: NextPageWithLayout<InferGetStaticPropsType<typeof getStaticProps>> = ({about, footer}) => {
  const {isMobile} = useBreakpoint('mobile');
  const environment = useEnvironment();
  return (
    <>
      <AboutNavigation active={'about'} />
      <Col className="items-center bg-primary-700 py-24 space-y-4 mobile:py-[10rem]">
        <div className="w-full max-w-limit px-sm text-primary-300 font-semibold text-[1.6rem]">ABOUT</div>
        <div className="w-full max-w-limit px-sm text-white text-huge font-semibold leading-tight">{about.title}</div>
      </Col>
      <Col className="w-full max-w-limit space-y-24 my-24 mx-auto px-sm">
        <div className="w-full text-3xl font-semibold">{about.subtitle}</div>
        <Row className="flex-nowrap mobile:overflow-x-scroll gap-sm mobile:-mx-sm mobile:px-sm !mt-10">
          {about.feature_cards?.map((fc, idx) => (
            <InfoCard
              key={`about-fc-${idx}`}
              className="flex-1 mobile:flex-none mobile:w-10/12 mobile:flex-shrink-0"
              icon={icons[fc.icon_id as IconId]}
              label={fc.text}
              dark={fc.dark}
            />
          ))}
        </Row>
        <RichText className="pl-40 mobile:pl-0 mx-auto" content={about.body} />
        <div className="mx-auto">
          <Image src={isMobile ? cropLeavesVert : cropLeaves} alt="crop leaves" />
        </div>
      </Col>
      <div className="w-full max-w-limit mx-auto">
        <Col className="space-y-4 bg-primary-100 rounded-[4rem] mobile:rounded-none mx-md mobile:mx-0">
          <div className="p-16">
            <div className="text-3xl font-semibold">{about.how_title}</div>
            <RichText className="mt-4" content={about.how_description} />
          </div>
          <div className="grid grid-cols-3 mobile:grid-cols-[30rem_30rem_30rem] gap-[3rem] overflow-x-auto px-16 mobile:px-md pb-16">
            {about.how_cards?.map((hc, idx) => (
              <InfoCard
                key={`about-hc-${idx}`}
                className="mobile:w-[30rem]"
                icon={icons[hc.icon_id as IconId]}
                label={hc.text}
                dark={hc.dark}
              />
            ))}
          </div>
        </Col>
        <Col className="gap-xl mx-20 my-2xl">
          {about.collaborators && <PartnerLogos title={about.collaborators_title} partners={about.collaborators} />}
          {about.partnerships && <PartnerLogos title={about.partnertships_title} partners={about.partnerships} />}
        </Col>
      </div>
      {environment && <Newsletter data={environment.newsletter} />}
      <Footer data={footer} />
    </>
  );
};

export const getStaticProps = async () => {
  return buildStaticPropsResponse({
    ...(await getCmsEntry<ApiAboutAbout, 'about'>({
      path: 'about',
      params: {populate: ['feature_cards', 'how_cards', 'collaborators.logo', 'partnerships.logo']},
      processDataMap: {
        body: 'markdown',
        how_description: 'markdown',
        collaborators: {logo: 'image'},
        partnerships: {logo: 'image'},
      },
    })),
    ...(await getCmsEntry<ApiFooterFooter, 'footer'>({path: 'footer'})),
  });
};

export default AboutPage;

AboutPage.getLayout = getToolLayout;

AboutPage.title = 'About';
